// API client for the subscriptions surface (GYM-86).
//
// Types and fetchers for sell-plan, freeze, and payment status endpoints.

import { apiFetch } from "@/lib/api/client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SellPlanMemberPayload {
  id?: string;
  full_name_en?: string;
  full_name_ar?: string;
  mobile_e164?: string;
  home_branch_id?: string;
}

export interface SellPlanSeatMember {
  id?: string;
  full_name_en: string;
  full_name_ar?: string;
  mobile: string;
  home_branch_id?: string;
}

export interface DiscretionaryDiscount {
  amount: string;
  reason: string;
}

export interface SellPlanRequest {
  idempotency_key: string;
  member: SellPlanMemberPayload;
  seat_members?: SellPlanSeatMember[];
  plan_id: string;
  start_date: string;
  promo_code?: string;
  discretionary_discount?: DiscretionaryDiscount;
  payment_method: "cash" | "bank_transfer" | "manual_card" | "telr_auto" | "telr_link" | "credit_note" | "paytabs" | "paytabs_link" | "stripe_installment" | "stripe";
  payment_details?: Record<string, string>;
  credit_note_id?: string;
  stripe_payment_method_id?: string;
  /** Manager PIN approving a discretionary discount above the configured cap. */
  discount_approval_pin?: string;
}

export interface SellPlanResponse {
  status: "completed" | "failed" | "pending";
  subscription_id: string | null;
  payment_id: string;
  invoice_id: string;
  error_code: string | null;
  error_message: string | null;
  /** Present when status="pending" and payment_method="telr_link" */
  checkout_url?: string | null;
  /** Sessions carried over from expired plans via PT bank */
  pt_sessions_rolled_over?: number;
}

export interface FreezeRequest {
  freeze_start: string;
  freeze_end: string;
  reason?: string;
}

export interface FreezeResponse {
  subscription_id: string;
  status: string;
  end_date: string;
  message: string;
}

export interface UnfreezeResponse {
  subscription_id: string;
  status: string;
  message: string;
}

export interface CancelRequest {
  refund_unused_days: boolean;
  reason?: string;
}

export interface CancelResponse {
  cancelled_subscription_id: string;
  credit_note_id: string | null;
  refund_amount: string;
  days_used: number;
  pt_consumed: number;
  pt_value: string;
}

export interface UpgradeRequest {
  new_plan_id: string;
  payment_method?: string;
  payment_details?: Record<string, string>;
}

export interface UpgradeResponse {
  old_subscription: Record<string, unknown>;
  new_subscription: Record<string, unknown>;
}

export interface PaymentStatusResponse {
  status: "pending" | "succeeded" | "failed" | "refunded" | "partially_refunded";
  gateway_charge_id: string | null;
  error_code: string | null;
  subscription_id: string | null;
}

export interface Plan {
  id: string;
  tenant: string;
  name_en: string;
  name_ar: string;
  plan_type: "gym_only" | "gym_plus_pt" | "pt_only" | "trial" | "day_pass" | "zone_access";
  included_zone_ids: string[];
  price: string;
  tax_pct: string;
  duration_days: number;
  max_freeze_days: number;
  min_freeze_days: number;
  pt_sessions_granted: number;
  pt_session_price: string;
  is_active: boolean;
  allows_group: boolean;
  max_seats: number;
  required_approval_level: 1 | 2 | 3;
  created_at: string;
  updated_at: string;
}

export interface PromoValidationRequest {
  plan_id: string;
}

export interface PromoValidationResponse {
  valid: boolean;
  discount_amount: string | null;
  error_code: string | null;
  error_message: string | null;
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

export async function sellPlan(data: SellPlanRequest): Promise<SellPlanResponse> {
  return apiFetch("/subscriptions/sell/", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export interface ExistingMembershipRequest {
  member_id: string;
  plan_id: string;
  /** Defaults server-side to end_date minus the plan duration. */
  start_date?: string;
  end_date: string;
}

export interface ExistingMembershipResponse {
  subscription_id: string;
  status: string;
  start_date: string;
  end_date: string;
}

/** Record a membership sold before the gym moved to GymViz (no charge, no invoice). */
export async function createExistingMembership(
  data: ExistingMembershipRequest
): Promise<ExistingMembershipResponse> {
  return apiFetch("/subscriptions/existing/", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function freezeSubscription(
  subscriptionId: string,
  data: FreezeRequest
): Promise<FreezeResponse> {
  return apiFetch(`/subscriptions/${subscriptionId}/freeze/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function unfreezeSubscription(
  subscriptionId: string
): Promise<UnfreezeResponse> {
  return apiFetch(`/subscriptions/${subscriptionId}/unfreeze/`, {
    method: "POST",
  });
}

export async function cancelSubscription(
  subscriptionId: string,
  data: CancelRequest
): Promise<CancelResponse> {
  return apiFetch(`/subscriptions/${subscriptionId}/cancel/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function upgradeSubscription(
  subscriptionId: string,
  data: UpgradeRequest
): Promise<UpgradeResponse> {
  return apiFetch(`/subscriptions/${subscriptionId}/upgrade/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getPaymentStatus(paymentId: string): Promise<PaymentStatusResponse> {
  return apiFetch(`/payments/${paymentId}/status/`, {
    method: "GET",
  });
}

export async function getPlans(): Promise<Plan[]> {
  return apiFetch("/plans/", {
    method: "GET",
  });
}

export async function validatePromoCode(
  code: string,
  data: PromoValidationRequest
): Promise<PromoValidationResponse> {
  return apiFetch(`/promotions/${code}/redeem-validate/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// GYM-791 / GYM-536: Auto-renew toggle

export interface AutoRenewToggleResponse {
  subscription_id: string;
  auto_renew: boolean;
  status: string;
  end_date: string;
  saved_card_last4: string | null;
}

export async function toggleAutoRenew(
  subscriptionId: string,
  autoRenew: boolean
): Promise<AutoRenewToggleResponse> {
  return apiFetch(`/subscriptions/${subscriptionId}/auto-renew/`, {
    method: "PATCH",
    body: JSON.stringify({ auto_renew: autoRenew }),
  });
}

export interface DunningItem {
  subscription_id: string;
  member_id: string;
  member_name: string;
  plan_name: string;
  status: string;
  failure_count: number;
  day_in_cycle: number;
  dunning_stage: number;
  first_failure_at: string | null;
  last_failure_at: string | null;
  end_date: string;
  branch_name: string;
}

export interface DunningListResponse {
  count: number;
  results: DunningItem[];
}

export async function getDunningList(): Promise<DunningListResponse> {
  return apiFetch("/subscriptions/dunning/", {
    method: "GET",
  });
}

export async function reactivateSubscription(
  subscriptionId: string
): Promise<{ subscription_id: string; status: string; message: string }> {
  return apiFetch(`/subscriptions/${subscriptionId}/reactivate/`, {
    method: "POST",
  });
}

// ---------------------------------------------------------------------------
// Self-service review queue (GYM-1417)
// ---------------------------------------------------------------------------

export interface FreezePayload {
  freeze_start: string;
  freeze_end: string;
  reason?: string;
}

export interface CancelPayload {
  reason?: string;
  refund_mode: "credit" | "money" | "none";
  cancel_reason_code?: string;
  cancel_reason_text?: string;
}

export interface MCRApprovalChainEntry {
  level: number;
  approver_name: string;
  status: "approved" | "rejected" | "pending" | "not_reached";
  actioned_at: string | null;
  remarks: string;
}

export interface SelfServiceRequestBase {
  id: string;
  status: "pending" | "approved" | "denied" | "expired";
  subscription_id: string;
  member_id: string;
  member_name: string;
  member_name_ar: string;
  plan_name: string;
  branch_id: string | null;
  branch_name: string;
  requested_at: string | null;
  approver_id: string | null;
  approved_at: string | null;
  approver_note: string | null;
  cancel_reason_code: string | null;
  cancel_reason_text: string | null;
  total_approvals: number;
  total_approved: number;
  approval_chain: MCRApprovalChainEntry[];
}

export interface SelfServiceFreezeRequest extends SelfServiceRequestBase {
  kind: "freeze";
  payload: FreezePayload;
}

export interface SelfServiceCancelRequest extends SelfServiceRequestBase {
  kind: "cancel";
  payload: CancelPayload;
}

export type SelfServiceRequest = SelfServiceFreezeRequest | SelfServiceCancelRequest;

export interface SelfServiceListResponse {
  count: number;
  results: SelfServiceRequest[];
}

export interface SelfServiceApproveResponse {
  status: "approved" | "level_approved";
  request_id: string;
  new_end_date?: string;
  credit_note_id?: string;
  refund_amount?: string;
  level?: number;
  total?: number;
}

export interface SelfServiceDenyResponse {
  status: "denied";
  request_id: string;
}

export async function getSelfServiceRequests(params: {
  status?: string;
  kind?: string;
  branch?: string;
} = {}): Promise<SelfServiceListResponse> {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.kind) qs.set("kind", params.kind);
  if (params.branch) qs.set("branch", params.branch);
  const query = qs.toString() ? `?${qs.toString()}` : "";
  return apiFetch(`/subscriptions/self-service-requests/${query}`, { method: "GET" });
}

export async function approveSelfServiceRequest(
  id: string,
  note?: string,
): Promise<SelfServiceApproveResponse> {
  return apiFetch(`/subscriptions/self-service-requests/${id}/approve/`, {
    method: "POST",
    body: JSON.stringify({ note: note ?? "" }),
  });
}

export async function denySelfServiceRequest(
  id: string,
  note: string,
): Promise<SelfServiceDenyResponse> {
  return apiFetch(`/subscriptions/self-service-requests/${id}/deny/`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

// ---------------------------------------------------------------------------
// Hold / Release-Hold (GYM-1431 / GYM-1523)
// ---------------------------------------------------------------------------

export interface HoldRequest {
  reason: string;
}

export interface HoldResponse {
  subscription_id: string;
  status: string;
  message: string;
}

export interface ReleaseHoldRequest {
  restore_days: boolean;
}

export interface ReleaseHoldResponse {
  subscription_id: string;
  status: string;
  end_date: string | null;
  message: string;
}

export async function holdSubscription(
  subscriptionId: string,
  data: HoldRequest,
): Promise<HoldResponse> {
  return apiFetch(`/subscriptions/${subscriptionId}/hold/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function releaseHoldSubscription(
  subscriptionId: string,
  data: ReleaseHoldRequest,
): Promise<ReleaseHoldResponse> {
  return apiFetch(`/subscriptions/${subscriptionId}/release-hold/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ---------------------------------------------------------------------------
// GYM-1442: Renew, Downgrade, Cancel-scheduled-downgrade
// ---------------------------------------------------------------------------

export interface RenewRequest {
  payment_method?: string;
  payment_details?: Record<string, string>;
}

export interface RenewResponse {
  old_subscription: Record<string, unknown>;
  new_subscription: Record<string, unknown>;
}

export async function renewSubscription(
  subscriptionId: string,
  data: RenewRequest = {},
): Promise<RenewResponse> {
  return apiFetch(`/subscriptions/${subscriptionId}/renew/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export interface DowngradeRequest {
  new_plan_id: string;
  payment_method?: string;
  payment_details?: Record<string, string>;
}

export interface DowngradeProration {
  remaining_value: string | null;
  new_value: string;
  delta: string | null;
  credit_note_id: string | null;
  scheduled: boolean;
}

export interface DowngradeResponse {
  old_subscription: Record<string, unknown>;
  new_subscription: Record<string, unknown> | null;
  proration: DowngradeProration;
}

export async function downgradeSubscription(
  subscriptionId: string,
  data: DowngradeRequest,
): Promise<DowngradeResponse> {
  return apiFetch(`/subscriptions/${subscriptionId}/downgrade/`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function cancelScheduledDowngrade(
  subscriptionId: string,
): Promise<{ subscription_id: string; message: string }> {
  return apiFetch(`/subscriptions/${subscriptionId}/cancel-downgrade/`, {
    method: "POST",
  });
}

export async function addPtSessions(
  subscriptionId: string,
  sessions: number,
  feePerSession?: number,
  trainerId?: string,
  paymentMethod?: string,
): Promise<{ subscription_id: string; pt_sessions_granted: number; invoice_id?: string; invoice_number?: string }> {
  return apiFetch(`/subscriptions/${subscriptionId}/add-pt-sessions/`, {
    method: "POST",
    body: JSON.stringify({
      sessions,
      ...(feePerSession != null ? { fee_per_session: feePerSession } : {}),
      ...(trainerId ? { trainer_id: trainerId } : {}),
      ...(paymentMethod ? { payment_method: paymentMethod } : {}),
    }),
  });
}
export interface PrevTrainerEarning {
  trainer_name: string;
  sessions: number;
  rate: string;
  earned: string;
}

export type CommissionInfo =
  | {
      type: "per_pt_session";
      rate: string;
      sessions_granted: number;
      /** Sessions completed by the current assigned trainer */
      sessions_completed_by_trainer: number;
      /** Total sessions completed across all trainers */
      sessions_completed_total: number;
      /** Sessions done by previous trainer(s) before this trainer was assigned */
      sessions_done_by_others: number;
      /** Sessions not yet done by anyone */
      sessions_remaining: number;
      /** Commission earned by this trainer so far (their own sessions × rate) */
      earned: string;
      /** Max potential: earned + remaining × rate */
      potential: string;
      /** Per-previous-trainer breakdown */
      prev_trainers: PrevTrainerEarning[];
    }
  | {
      type: "percent_of_pt_plans_sold";
      percent: string;
      sale_amount: string;
      commission: string;
    };

export interface PtSaleItem {
  id: string;
  member_id: string | null;
  member_name: string;
  plan_name: string;
  plan_type: "pt_only" | "gym_plus_pt";
  start_date: string | null;
  status: string;
  pt_sessions_granted: number;
  sessions_completed: number;
  price: string;
  /** Discount given at sale time (0 when sold at list price) */
  discount_amount: string;
  /** Amount actually paid (list price − discount) */
  paid_amount: string;
  trainer_id: string | null;
  trainer_name: string | null;
  commission_info: CommissionInfo | null;
}

export interface PtSalesResponse {
  results: PtSaleItem[];
  total: number;
  count: number;
  page: number;
  page_size: number;
  num_pages: number;
  unassigned_count: number;
}

export interface PtSalesFilters {
  date_from?: string;
  date_to?: string;
  trainer_id?: string;
  unassigned?: boolean;
  member_name?: string;
  page?: number;
  page_size?: number;
}

export async function listPtSales(filters: PtSalesFilters = {}): Promise<PtSalesResponse> {
  const params = new URLSearchParams();
  if (filters.date_from) params.set("date_from", filters.date_from);
  if (filters.date_to) params.set("date_to", filters.date_to);
  if (filters.trainer_id) params.set("trainer_id", filters.trainer_id);
  if (filters.unassigned) params.set("unassigned", "true");
  if (filters.member_name) params.set("member_name", filters.member_name);
  if (filters.page && filters.page > 1) params.set("page", String(filters.page));
  if (filters.page_size) params.set("page_size", String(filters.page_size));
  const qs = params.toString();
  return apiFetch(`/subscriptions/pt-sales/${qs ? `?${qs}` : ""}`);
}

export async function assignPtTrainer(
  subscriptionId: string,
  trainerId: string,
): Promise<{ subscription_id: string; trainer_id: string; trainer_name: string }> {
  return apiFetch(`/subscriptions/${subscriptionId}/assign-trainer/`, {
    method: "PATCH",
    body: JSON.stringify({ trainer_id: trainerId }),
  });
}
